import { AiAgent, AgentListMeta } from 'services/agentBuilder'
import { ByoAgent, ByoIntegration, ByoTool, DynamicFormSchema } from 'services/byoAgents'
import actions from './actions'

/**
 * AI Agents slice state. The builder keys the list page reads are typed strictly;
 * the agent-config keys stay loose until that surface migrates.
 */
export interface AiAgentsState {
  // AI Agents Builder — read by pages/app/ai-agents
  aiAgentsBuilderList: AiAgent[]
  aiAgentsBuilderListPagination: AgentListMeta | Record<string, never>
  areAiAgentsBuildersLoading: boolean
  deletingAgentBuilderIds: string[]
  updatingAgentBuilderIds: string[]
  activeBuilderAgent: AiAgent | null
  isActiveBuilderAgentLoading: boolean
  isAiAgentBuilderCreateFormLoading: boolean
  lastCreatedAgentId: string | null
  // Bring Your Own Agents — read by the BYO Agents / Actions / Tools pages
  aiAgentsList: ByoAgent[]
  areAiAgentsLoading: boolean
  deletingAgentIds: string[]
  updatingAgentIds: string[]
  aiAgentCreateForm: DynamicFormSchema | null
  isAiAgentCreateFormLoading: boolean
  aiAgentEditForm: DynamicFormSchema | null
  isAiAgentEditFormLoading: boolean
  aiAgentIntegrations: ByoIntegration[]
  isAiAgentIntegrationsLoading: boolean
  aiToolsList: ByoTool[]
  areAiToolsLoading: boolean
  aiActionsList: ByoTool[]
  areAiActionsLoading: boolean
  deletingToolNames: string[]
  aiToolCreateForm: DynamicFormSchema | null
  isAiToolCreateFormLoading: boolean
  aiToolEditForm: DynamicFormSchema | null
  isAiToolEditFormLoading: boolean
  aiActionCreateForm: DynamicFormSchema | null
  isAiActionCreateFormLoading: boolean
  aiActionEditForm: DynamicFormSchema | null
  isAiActionEditFormLoading: boolean
  isByoFormSubmitting: boolean
  // Everything else belongs to the un-migrated agent-config surface.
  [key: string]: unknown
}

const initialState: AiAgentsState = {
  // AI Agents
  aiAgentsList: [],
  areAiAgentsLoading: false,
  deletingAgentIds: [],
  updatingAgentIds: [],
  aiAgentCreateForm: null,
  isAiAgentCreateFormLoading: false,
  aiAgentEditForm: null,
  isAiAgentEditFormLoading: false,
  aiAgentIntegrations: [],
  isAiAgentIntegrationsLoading: false,
  lastCreatedAgentId: null,
  // AI Agents Builder
  aiAgentsBuilderList: [],
  aiAgentsBuilderListTotal: 0,
  aiAgentsBuilderListPagination: {},
  aiAgentBuilderSearchText: '',
  areAiAgentsBuildersLoading: false,
  deletingAgentBuilderIds: [],
  updatingAgentBuilderIds: [],
  activeBuilderAgent: null,
  isActiveBuilderAgentLoading: false,
  isAiAgentBuilderCreateFormLoading: false,
  // AI Tools
  aiToolsList: [],
  areAiToolsLoading: false,
  aiToolEditForm: null,
  isAiToolEditFormLoading: false,
  aiToolCreateForm: null,
  isAiToolCreateFormLoading: false,
  // AI Actions
  aiActionsList: [],
  areAiActionsLoading: false,
  aiActionEditForm: null,
  isAiActionEditFormLoading: false,
  aiActionCreateForm: null,
  isAiActionCreateFormLoading: false,
  deletingToolNames: [],
  isByoFormSubmitting: false,
  // AI Tools Builder
  aiToolsBuilderList: [],
  aiToolsBuilderListTotal: 0,
  aiToolsBuilderListPagination: {},
  aiToolBuilderSearchText: '',
  areAiToolsBuildersLoading: false,
  currentAiToolBuilder: null,
  isCurrentAiToolBuilderLoading: false,
  aiToolBuilderEditForm: null,
  isAiToolBuilderEditFormLoading: false,
  aiActionBuilderEditForm: null,
  isAiActionBuilderEditFormLoading: false,
  aiToolBuilderCreateForm: null,
  isAiToolBuilderCreateFormLoading: false,
  aiToolActionsList: [],
  aiToolActionsTotal: 0,
  aiToolActionsLoading: false,
  aiToolActionsPagination: {},
  currentAiToolActions: null,
  aiActionBuilderCreateForm: null,
  isAiActionBuilderCreateFormLoading: false,
  // Custom API Tools
  customApiToolsList: [],
  customApiToolsTotal: 0,
  customApiToolsLoading: false,
  customApiToolCreating: false,
  customApiToolUpdating: false,
  customApiToolsPagination: {},
  // Knowledge Base
  knowledgeBaseList: [],
  knowledgeBaseTotal: 0,
  knowledgeBaseLoading: false,
  knowledgeBasePagination: {},
  knowledgeBaseDetails: null,
  knowledgeBaseDetailsLoading: false,
  knowledgeBaseDetailsPagination: {},
  // MCP Servers
  mcpServersList: [],
  mcpServersTotal: 0,
  mcpServersLoading: false,
  mcpServersPagination: {},
  // Frontend Actions
  frontendActionsList: [],
  frontendActionsTotal: 0,
  frontendActionsLoading: false,
  frontendActionsError: null,
  frontendActionsPagination: {},
  frontendActionFormLoading: false,
  frontendActionDeleteLoading: false,
  // Available Models
  availableModels: [],
  availableModelsLoading: false,
  // AI Agent Users
  aiAgentUserList: [],
  aiAgentUserListTotal: 0,
  aiAgentUserListText: '',
  aiAgentUserSearchText: '',
  areAIAgentUsersLoading: false,
  aiAgentUserListPagination: {},
}

interface AiAgentsAction {
  type: string
  payload?: Partial<AiAgentsState>
}

export default function aiAgentsReducer(
  state: AiAgentsState = initialState,
  action: AiAgentsAction = { type: '' },
): AiAgentsState {
  switch (action.type) {
    case actions.SET_STATE:
      return { ...state, ...action.payload }
    case actions.INITIALIZE_PAGE:
      // Mirrors the `apps` slice's agent keys `apps/INITIALIZE_AI_AGENTS_BUILDER_PAGE`;
      // without it a stale cursor survives navigation away and back.
      return { ...state, lastCreatedAgentId: null, aiAgentsBuilderListPagination: {} }
    default:
      return state
  }
}
